/**
 * Error message mapping utilities for user-friendly error display
 *
 * This module provides utilities for mapping error codes to user-friendly
 * messages and determining appropriate UI actions for different error types.
 */

import type { BaseError } from "../classes/BaseError";
import {
  VALIDATION_ERROR_CODES,
  AUTHENTICATION_ERROR_CODES,
  AUTHORIZATION_ERROR_CODES,
  NOT_FOUND_ERROR_CODES,
  CONFLICT_ERROR_CODES,
  INTERNAL_ERROR_CODES,
  RATE_LIMIT_ERROR_CODES,
  NETWORK_ERROR_CODES,
  DATABASE_ERROR_CODES,
  EXTERNAL_SERVICE_ERROR_CODES,
  FRONTEND_ERROR_CODES,
} from "../constants/errorCodes";

/**
 * User-friendly error message mapping
 */
export const USER_FRIENDLY_MESSAGES: Record<string, string> = {
  // Frontend errors
  [FRONTEND_ERROR_CODES.FRONTEND_ERROR]: "An error occurred. Please try again.",

  // Validation errors
  [VALIDATION_ERROR_CODES.VALIDATION_ERROR]: "Please check your input and try again.",
  [VALIDATION_ERROR_CODES.INVALID_INPUT]: "The information you entered is not valid.",
  [VALIDATION_ERROR_CODES.INVALID_FORMAT]: "The format of your input is incorrect.",
  [VALIDATION_ERROR_CODES.MISSING_REQUIRED_FIELD]: "This field is required.",
  [VALIDATION_ERROR_CODES.VALUE_OUT_OF_RANGE]: "The value is outside the allowed range.",
  [VALIDATION_ERROR_CODES.VALUE_TOO_LONG]: "The value is too long.",
  [VALIDATION_ERROR_CODES.VALUE_TOO_SHORT]: "The value is too short.",
  [VALIDATION_ERROR_CODES.INVALID_CHARACTERS]: "The value contains invalid characters.",

  // Authentication errors
  [AUTHENTICATION_ERROR_CODES.AUTHENTICATION_ERROR]: "Authentication failed. Please try again.",
  [AUTHENTICATION_ERROR_CODES.AUTHENTICATION_FAILED]:
    "Login failed. Please check your credentials.",
  [AUTHENTICATION_ERROR_CODES.INVALID_TOKEN]: "Your session is invalid. Please log in again.",
  [AUTHENTICATION_ERROR_CODES.TOKEN_EXPIRED]: "Your session has expired. Please log in again.",
  [AUTHENTICATION_ERROR_CODES.TOKEN_MISSING]: "Please log in to continue.",
  [AUTHENTICATION_ERROR_CODES.ACCOUNT_LOCKED]:
    "Your account has been locked. Please contact support.",
  [AUTHENTICATION_ERROR_CODES.ACCOUNT_UNVERIFIED]:
    "Your account needs to be verified. Please check your email.",
  [AUTHENTICATION_ERROR_CODES.TOO_MANY_ATTEMPTS]:
    "Too many failed attempts. Please try again later.",
  [AUTHENTICATION_ERROR_CODES.TWO_FACTOR_REQUIRED]: "Two-factor authentication is required.",
  [AUTHENTICATION_ERROR_CODES.TWO_FACTOR_FAILED]:
    "Two-factor authentication failed. Please try again.",

  // Authorization errors
  [AUTHORIZATION_ERROR_CODES.AUTHORIZATION_ERROR]: "Authorization failed. Please try again.",
  [AUTHORIZATION_ERROR_CODES.AUTHORIZATION_DENIED]:
    "You don't have permission to perform this action.",
  [AUTHORIZATION_ERROR_CODES.ACCESS_FORBIDDEN]: "Access denied. Please contact your administrator.",
  [AUTHORIZATION_ERROR_CODES.INSUFFICIENT_PERMISSIONS]:
    "You need additional permissions to perform this action.",
  [AUTHORIZATION_ERROR_CODES.ORGANIZATION_ACCESS_DENIED]:
    "You don't have access to this organization.",
  [AUTHORIZATION_ERROR_CODES.TEAM_ACCESS_DENIED]: "You don't have access to this team.",
  [AUTHORIZATION_ERROR_CODES.ROLE_ACCESS_DENIED]:
    "You need a specific role to perform this action.",

  // Not found errors
  [NOT_FOUND_ERROR_CODES.NOT_FOUND_ERROR]: "The requested item could not be found.",
  [NOT_FOUND_ERROR_CODES.ENTITY_NOT_FOUND]: "The requested item could not be found.",
  [NOT_FOUND_ERROR_CODES.USER_NOT_FOUND]: "User not found.",
  [NOT_FOUND_ERROR_CODES.ORGANIZATION_NOT_FOUND]: "Organization not found.",
  [NOT_FOUND_ERROR_CODES.TEAM_NOT_FOUND]: "Team not found.",
  [NOT_FOUND_ERROR_CODES.RESOURCE_NOT_FOUND]: "The requested resource could not be found.",
  [NOT_FOUND_ERROR_CODES.FILE_NOT_FOUND]: "File not found.",
  [NOT_FOUND_ERROR_CODES.ROUTE_NOT_FOUND]: "Route not found.",
  [NOT_FOUND_ERROR_CODES.ENDPOINT_NOT_FOUND]: "API endpoint not found.",

  // Conflict errors
  [CONFLICT_ERROR_CODES.CONFLICT_ERROR]: "This action conflicts with existing data.",
  [CONFLICT_ERROR_CODES.DUPLICATE_ENTITY]: "This item already exists.",
  [CONFLICT_ERROR_CODES.EMAIL_ALREADY_EXISTS]: "An account with this email already exists.",
  [CONFLICT_ERROR_CODES.USERNAME_ALREADY_EXISTS]: "An account with this username already exists.",
  [CONFLICT_ERROR_CODES.RESOURCE_ALREADY_EXISTS]: "This resource already exists.",
  [CONFLICT_ERROR_CODES.CONCURRENT_MODIFICATION]: "The resource was modified by another user.",
  [CONFLICT_ERROR_CODES.VERSION_CONFLICT]: "Version conflict detected.",
  [CONFLICT_ERROR_CODES.STATE_CONFLICT]: "Invalid state for this operation.",

  // Rate limit errors
  [RATE_LIMIT_ERROR_CODES.RATE_LIMIT_EXCEEDED]:
    "Too many requests. Please wait a moment and try again.",
  [RATE_LIMIT_ERROR_CODES.TOO_MANY_REQUESTS]:
    "You're making requests too quickly. Please slow down.",
  [RATE_LIMIT_ERROR_CODES.QUOTA_EXCEEDED]:
    "You've reached your usage limit. Please try again later.",
  [RATE_LIMIT_ERROR_CODES.RESOURCE_EXHAUSTED]:
    "System resources are exhausted. Please try again later.",
  [RATE_LIMIT_ERROR_CODES.BANDWIDTH_LIMIT_EXCEEDED]:
    "Bandwidth limit exceeded. Please try again later.",

  // Network errors
  [NETWORK_ERROR_CODES.NETWORK_ERROR]: "Network error. Please check your connection and try again.",
  [NETWORK_ERROR_CODES.CONNECTION_TIMEOUT]: "Connection timed out. Please check your network.",
  [NETWORK_ERROR_CODES.REQUEST_TIMEOUT]: "Request timed out. Please try again.",
  [NETWORK_ERROR_CODES.CONNECTION_REFUSED]:
    "Unable to connect to the server. Please try again later.",
  [NETWORK_ERROR_CODES.NETWORK_UNREACHABLE]:
    "Network is unreachable. Please check your connection.",

  // Database errors
  [DATABASE_ERROR_CODES.DATABASE_ERROR]: "A database error occurred. Please try again.",
  [DATABASE_ERROR_CODES.DATABASE_CONNECTION_FAILED]:
    "Database connection failed. Please try again later.",
  [DATABASE_ERROR_CODES.DATABASE_QUERY_FAILED]: "Database query failed. Please try again.",
  [DATABASE_ERROR_CODES.DATABASE_TRANSACTION_FAILED]:
    "Database transaction failed. Please try again.",
  [DATABASE_ERROR_CODES.DATABASE_CONSTRAINT_VIOLATION]:
    "Data conflict detected. Please check your input.",
  [DATABASE_ERROR_CODES.DATABASE_DEADLOCK]: "System is busy. Please try again in a moment.",

  // External service errors
  [EXTERNAL_SERVICE_ERROR_CODES.EXTERNAL_SERVICE_ERROR]:
    "External service error. Please try again later.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_UNAVAILABLE]:
    "Service temporarily unavailable. Please try again later.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_TIMEOUT]: "Service request timed out. Please try again.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_RATE_LIMIT_EXCEEDED]:
    "External service rate limit exceeded. Please try again later.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_AUTHENTICATION_FAILED]:
    "External service authentication failed. Please try again later.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_AUTHORIZATION_FAILED]:
    "External service authorization failed. Please try again later.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_QUOTA_EXCEEDED]:
    "External service quota exceeded. Please try again later.",

  // Internal errors
  [INTERNAL_ERROR_CODES.INTERNAL_ERROR]: "An internal error occurred. Please try again.",
  [INTERNAL_ERROR_CODES.UNEXPECTED_ERROR]: "An unexpected error occurred. Please try again.",
  [INTERNAL_ERROR_CODES.SYSTEM_ERROR]: "System error. Please try again later.",
  [INTERNAL_ERROR_CODES.CONFIGURATION_ERROR]: "Configuration error. Please contact support.",
  [INTERNAL_ERROR_CODES.NOT_IMPLEMENTED]: "This feature is not yet implemented.",
  [INTERNAL_ERROR_CODES.TEMPORARILY_UNAVAILABLE]:
    "Service temporarily unavailable. Please try again later.",
};

/**
 * Error action mapping - what the user should do for each error type
 */
export const ERROR_ACTIONS: Record<string, string> = {
  // Validation errors
  [VALIDATION_ERROR_CODES.VALIDATION_ERROR]: "Please correct the highlighted fields and try again.",
  [VALIDATION_ERROR_CODES.INVALID_INPUT]: "Please check your input and try again.",
  [VALIDATION_ERROR_CODES.INVALID_FORMAT]: "Please use the correct format and try again.",
  [VALIDATION_ERROR_CODES.MISSING_REQUIRED_FIELD]: "Please fill in all required fields.",
  [VALIDATION_ERROR_CODES.VALUE_OUT_OF_RANGE]: "Please enter a value within the allowed range.",
  [VALIDATION_ERROR_CODES.VALUE_TOO_LONG]: "Please enter a shorter value.",
  [VALIDATION_ERROR_CODES.VALUE_TOO_SHORT]: "Please enter a longer value.",
  [VALIDATION_ERROR_CODES.INVALID_CHARACTERS]: "Please remove invalid characters.",

  // Authentication errors
  [AUTHENTICATION_ERROR_CODES.AUTHENTICATION_ERROR]: "Please try logging in again.",
  [AUTHENTICATION_ERROR_CODES.AUTHENTICATION_FAILED]:
    "Please check your credentials and try again.",
  [AUTHENTICATION_ERROR_CODES.INVALID_TOKEN]: "Please log in again to continue.",
  [AUTHENTICATION_ERROR_CODES.TOKEN_EXPIRED]: "Please log in again to continue.",
  [AUTHENTICATION_ERROR_CODES.TOKEN_MISSING]: "Please log in to continue.",
  [AUTHENTICATION_ERROR_CODES.ACCOUNT_LOCKED]: "Please contact support to unlock your account.",
  [AUTHENTICATION_ERROR_CODES.ACCOUNT_UNVERIFIED]: "Please verify your account via email.",
  [AUTHENTICATION_ERROR_CODES.TOO_MANY_ATTEMPTS]: "Please wait before trying again.",
  [AUTHENTICATION_ERROR_CODES.TWO_FACTOR_REQUIRED]: "Please complete two-factor authentication.",
  [AUTHENTICATION_ERROR_CODES.TWO_FACTOR_FAILED]: "Please try two-factor authentication again.",

  // Authorization errors
  [AUTHORIZATION_ERROR_CODES.AUTHORIZATION_ERROR]: "Please contact your administrator for access.",
  [AUTHORIZATION_ERROR_CODES.AUTHORIZATION_DENIED]: "Please contact your administrator for access.",
  [AUTHORIZATION_ERROR_CODES.ACCESS_FORBIDDEN]: "Please contact your administrator for access.",
  [AUTHORIZATION_ERROR_CODES.INSUFFICIENT_PERMISSIONS]:
    "Please request additional permissions from your administrator.",
  [AUTHORIZATION_ERROR_CODES.ORGANIZATION_ACCESS_DENIED]:
    "Please contact your administrator for organization access.",
  [AUTHORIZATION_ERROR_CODES.TEAM_ACCESS_DENIED]:
    "Please contact your administrator for team access.",
  [AUTHORIZATION_ERROR_CODES.ROLE_ACCESS_DENIED]:
    "Please request the required role from your administrator.",

  // Not found errors
  [NOT_FOUND_ERROR_CODES.NOT_FOUND_ERROR]: "The requested item could not be found.",
  [NOT_FOUND_ERROR_CODES.ENTITY_NOT_FOUND]: "The item may have been deleted or moved.",
  [NOT_FOUND_ERROR_CODES.USER_NOT_FOUND]: "The user may have been deleted or the ID is incorrect.",
  [NOT_FOUND_ERROR_CODES.ORGANIZATION_NOT_FOUND]:
    "The organization may have been deleted or the ID is incorrect.",
  [NOT_FOUND_ERROR_CODES.TEAM_NOT_FOUND]: "The team may have been deleted or the ID is incorrect.",
  [NOT_FOUND_ERROR_CODES.RESOURCE_NOT_FOUND]: "The resource may have been deleted or moved.",
  [NOT_FOUND_ERROR_CODES.FILE_NOT_FOUND]: "The file may have been deleted or moved.",
  [NOT_FOUND_ERROR_CODES.ROUTE_NOT_FOUND]: "The requested page could not be found.",
  [NOT_FOUND_ERROR_CODES.ENDPOINT_NOT_FOUND]: "The requested API endpoint could not be found.",

  // Conflict errors
  [CONFLICT_ERROR_CODES.CONFLICT_ERROR]: "Please refresh the page and try again.",
  [CONFLICT_ERROR_CODES.DUPLICATE_ENTITY]: "Please use a different name or identifier.",
  [CONFLICT_ERROR_CODES.EMAIL_ALREADY_EXISTS]:
    "Please use a different email address or try logging in.",
  [CONFLICT_ERROR_CODES.USERNAME_ALREADY_EXISTS]: "Please use a different username.",
  [CONFLICT_ERROR_CODES.RESOURCE_ALREADY_EXISTS]:
    "This resource already exists. Please use a different identifier.",
  [CONFLICT_ERROR_CODES.CONCURRENT_MODIFICATION]:
    "The resource was modified by another user. Please refresh and try again.",
  [CONFLICT_ERROR_CODES.VERSION_CONFLICT]:
    "Version conflict detected. Please refresh and try again.",
  [CONFLICT_ERROR_CODES.STATE_CONFLICT]:
    "Invalid state for this operation. Please refresh and try again.",

  // Rate limit errors
  [RATE_LIMIT_ERROR_CODES.RATE_LIMIT_EXCEEDED]: "Please wait a moment before trying again.",
  [RATE_LIMIT_ERROR_CODES.TOO_MANY_REQUESTS]: "Please slow down your requests.",
  [RATE_LIMIT_ERROR_CODES.QUOTA_EXCEEDED]: "Please try again later or contact support.",
  [RATE_LIMIT_ERROR_CODES.RESOURCE_EXHAUSTED]:
    "System resources are exhausted. Please try again later.",
  [RATE_LIMIT_ERROR_CODES.BANDWIDTH_LIMIT_EXCEEDED]:
    "Bandwidth limit exceeded. Please try again later.",

  // Network errors
  [NETWORK_ERROR_CODES.NETWORK_ERROR]: "Please check your internet connection and try again.",
  [NETWORK_ERROR_CODES.CONNECTION_TIMEOUT]: "Please check your network connection and try again.",
  [NETWORK_ERROR_CODES.REQUEST_TIMEOUT]: "The request took too long. Please try again.",
  [NETWORK_ERROR_CODES.CONNECTION_REFUSED]:
    "The server is temporarily unavailable. Please try again later.",
  [NETWORK_ERROR_CODES.NETWORK_UNREACHABLE]:
    "Network is unreachable. Please check your connection.",

  // Database errors
  [DATABASE_ERROR_CODES.DATABASE_ERROR]:
    "Please try again. If the problem persists, contact support.",
  [DATABASE_ERROR_CODES.DATABASE_CONNECTION_FAILED]:
    "The system is temporarily unavailable. Please try again later.",
  [DATABASE_ERROR_CODES.DATABASE_QUERY_FAILED]: "Database query failed. Please try again.",
  [DATABASE_ERROR_CODES.DATABASE_TRANSACTION_FAILED]:
    "Database transaction failed. Please try again.",
  [DATABASE_ERROR_CODES.DATABASE_CONSTRAINT_VIOLATION]: "Please check your input for conflicts.",
  [DATABASE_ERROR_CODES.DATABASE_DEADLOCK]: "The system is busy. Please try again in a moment.",

  // External service errors
  [EXTERNAL_SERVICE_ERROR_CODES.EXTERNAL_SERVICE_ERROR]:
    "External service is temporarily unavailable. Please try again later.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_UNAVAILABLE]:
    "Service is temporarily unavailable. Please try again later.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_TIMEOUT]: "Service request timed out. Please try again.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_RATE_LIMIT_EXCEEDED]:
    "External service rate limit exceeded. Please try again later.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_AUTHENTICATION_FAILED]:
    "External service authentication failed. Please try again later.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_AUTHORIZATION_FAILED]:
    "External service authorization failed. Please try again later.",
  [EXTERNAL_SERVICE_ERROR_CODES.SERVICE_QUOTA_EXCEEDED]:
    "External service quota exceeded. Please try again later.",

  // Internal errors
  [INTERNAL_ERROR_CODES.INTERNAL_ERROR]:
    "Please try again. If the problem persists, contact support.",
  [INTERNAL_ERROR_CODES.UNEXPECTED_ERROR]: "An unexpected error occurred. Please try again.",
  [INTERNAL_ERROR_CODES.SYSTEM_ERROR]: "System error. Please try again later or contact support.",
  [INTERNAL_ERROR_CODES.CONFIGURATION_ERROR]: "Configuration error. Please contact support.",
  [INTERNAL_ERROR_CODES.NOT_IMPLEMENTED]: "This feature is not yet implemented.",
  [INTERNAL_ERROR_CODES.TEMPORARILY_UNAVAILABLE]:
    "Service temporarily unavailable. Please try again later.",
};

/**
 * Error category display names
 */
export const ERROR_CATEGORY_NAMES: Record<string, string> = {
  validation: "Validation Error",
  authentication: "Authentication Error",
  authorization: "Authorization Error",
  not_found: "Not Found",
  conflict: "Conflict",
  rate_limit: "Rate Limit",
  network: "Network Error",
  database: "Database Error",
  external_service: "External Service Error",
  internal: "System Error",
  business_logic: "Business Logic Error",
};

/**
 * Get user-friendly error message for an error
 */
export function getUserFriendlyMessage(error: BaseError): string {
  // First try to get a mapped message
  const mappedMessage = USER_FRIENDLY_MESSAGES[error.code];
  if (mappedMessage) {
    return mappedMessage;
  }

  // Handle specific error messages that might not have proper codes
  const message = error.message.toLowerCase();
  if (message.includes("invalid email or password") || message.includes("invalid credentials")) {
    return USER_FRIENDLY_MESSAGES[AUTHENTICATION_ERROR_CODES.AUTHENTICATION_FAILED];
  }
  if (message.includes("email already exists")) {
    return USER_FRIENDLY_MESSAGES[CONFLICT_ERROR_CODES.EMAIL_ALREADY_EXISTS];
  }
  if (message.includes("user not found")) {
    return USER_FRIENDLY_MESSAGES[NOT_FOUND_ERROR_CODES.USER_NOT_FOUND];
  }

  // If no mapping exists, use the error's message if it's user-visible
  if (error.userVisible) {
    return error.message;
  }

  // Default fallback message
  return "An error occurred. Please try again.";
}

/**
 * Get suggested action for an error
 */
export function getErrorAction(error: BaseError): string {
  const action = ERROR_ACTIONS[error.code];
  if (action) {
    return action;
  }

  // Default actions based on error category
  switch (error.category) {
    case "validation":
      return "Please check your input and try again.";
    case "authentication":
      return "Please log in again to continue.";
    case "authorization":
      return "Please contact your administrator for access.";
    case "not_found":
      return "The requested item could not be found.";
    case "conflict":
      return "Please refresh the page and try again.";
    case "rate_limit":
      return "Please wait a moment before trying again.";
    case "network":
      return "Please check your connection and try again.";
    case "database":
    case "external_service":
    case "internal":
      return "Please try again. If the problem persists, contact support.";
    default:
      return "Please try again.";
  }
}

/**
 * Get error category display name
 */
export function getErrorCategoryName(error: BaseError): string {
  return ERROR_CATEGORY_NAMES[error.category] || "Error";
}

/**
 * Check if an error should show a retry button
 */
export function shouldShowRetryButton(error: BaseError): boolean {
  // Show retry for network errors, rate limits, and some server errors
  return (
    error.category === "network" ||
    error.category === "rate_limit" ||
    (error.category === "internal" && (error.statusCode ?? 0) >= 500) ||
    error.category === "external_service"
  );
}

/**
 * Check if an error should redirect to login
 */
export function shouldRedirectToLogin(error: BaseError): boolean {
  return (
    error.category === "authentication" &&
    (error.code === AUTHENTICATION_ERROR_CODES.TOKEN_EXPIRED ||
      error.code === AUTHENTICATION_ERROR_CODES.INVALID_TOKEN ||
      error.code === AUTHENTICATION_ERROR_CODES.TOKEN_MISSING)
  );
}

/**
 * Check if an error should show a contact support option
 */
export function shouldShowContactSupport(error: BaseError): boolean {
  return (
    error.category === "internal" ||
    error.category === "database" ||
    error.category === "external_service" ||
    error.severity === "critical" ||
    error.severity === "high"
  );
}

/**
 * Get error icon name for UI display
 */
export function getErrorIcon(error: BaseError): string {
  switch (error.category) {
    case "validation":
      return "alert-circle";
    case "authentication":
      return "lock";
    case "authorization":
      return "shield";
    case "not_found":
      return "search";
    case "conflict":
      return "alert-triangle";
    case "rate_limit":
      return "clock";
    case "network":
      return "wifi-off";
    case "database":
      return "database";
    case "external_service":
      return "external-link";
    case "internal":
      return "alert-octagon";
    default:
      return "alert-circle";
  }
}

/**
 * Get error color for UI display
 */
export function getErrorColor(error: BaseError): string {
  switch (error.severity) {
    case "critical":
      return "red";
    case "high":
      return "orange";
    case "medium":
      return "yellow";
    case "low":
      return "blue";
    default:
      return "gray";
  }
}
